import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Settings, Save, Image, User, FileSignature, Link, RefreshCw,
  Award, Shield, CheckCircle, Eye, Upload, Globe, Building2, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCertificateSettingsPage = () => {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  
  // File input refs
  const logoInputRef = useRef(null);
  const signatureInputRef = useRef(null);
  const watermarkInputRef = useRef(null);
  const skillIndiaLogoInputRef = useRef(null);
  
  const [settings, setSettings] = useState({
    signatory_name: '',
    signatory_designation: '',
    signatory_signature_url: '',
    iqra_logo_url: '',
    skill_india_logo_url: '',
    watermark_url: '',
    verification_base_url: '',
    default_place: 'Jaipur',
    rpl_prefix: 'IQRA/RPL',
    stt_prefix: 'IQRA/STT',
    current_year: 2026,
    rpl_counter: 0,
    stt_counter: 0
  });

  useEffect(() => {
    if (!token || user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/auth/login');
      return;
    }
    fetchSettings();
  }, [token, user]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/certificate/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/certificate/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Handle file upload
  const handleFileUpload = async (file, assetType) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only image files (PNG, JPG, WEBP, GIF) are allowed');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setUploading(prev => ({ ...prev, [assetType]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/certificate/upload-asset?asset_type=${assetType}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Backend stores asset in DB and returns a relative /api URL that works on any domain
      if (response.data.field) {
        setSettings(prev => ({ ...prev, [response.data.field]: response.data.url }));
      }
      
      toast.success(response.data.message);
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [assetType]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <Settings className="w-7 h-7 text-blue-600" />
              Certificate Settings
            </h1>
            <p className="text-slate-600 mt-1">
              Manage certificate templates, logos, and signatory details
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} size="lg">
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Signatory Details Card */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-4 text-white">
              <h2 className="font-bold flex items-center gap-2">
                <FileSignature className="w-5 h-5" /> Signatory Details
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>Signatory Name / हस्ताक्षरकर्ता का नाम</Label>
                <Input
                  value={settings.signatory_name}
                  onChange={(e) => handleChange('signatory_name', e.target.value)}
                  placeholder="e.g., Ab Wahab"
                />
              </div>
              <div>
                <Label>Designation / पदनाम</Label>
                <Input
                  value={settings.signatory_designation}
                  onChange={(e) => handleChange('signatory_designation', e.target.value)}
                  placeholder="e.g., CEO, IQRA Skills Pvt Ltd"
                />
              </div>
              <div>
                <Label>Signature Image / हस्ताक्षर छवि</Label>
                <div className="flex gap-2">
                  <Input
                    value={settings.signatory_signature_url}
                    onChange={(e) => handleChange('signatory_signature_url', e.target.value)}
                    placeholder="https://... or upload"
                    className="flex-1"
                  />
                  <input
                    type="file"
                    ref={signatureInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'signature')}
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => signatureInputRef.current?.click()}
                    disabled={uploading.signature}
                    className="shrink-0"
                  >
                    {uploading.signature ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {settings.signatory_signature_url && (
                  <div className="mt-2 p-3 bg-slate-900 rounded-lg inline-block">
                    <img 
                      src={settings.signatory_signature_url} 
                      alt="Signature Preview" 
                      className="h-16 object-contain"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Logos Card */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 text-white">
              <h2 className="font-bold flex items-center gap-2">
                <Image className="w-5 h-5" /> Logos & Branding / लोगो और ब्रांडिंग
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>IQRA Skills Logo / इकरा स्किल्स लोगो</Label>
                <div className="flex gap-2">
                  <Input
                    value={settings.iqra_logo_url}
                    onChange={(e) => handleChange('iqra_logo_url', e.target.value)}
                    placeholder="https://... or upload"
                    className="flex-1"
                  />
                  <input
                    type="file"
                    ref={logoInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'logo')}
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploading.logo}
                    className="shrink-0"
                  >
                    {uploading.logo ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {settings.iqra_logo_url && (
                  <div className="mt-2 p-2 bg-white border rounded-lg inline-block">
                    <img 
                      src={settings.iqra_logo_url} 
                      alt="IQRA Logo Preview" 
                      className="h-12 object-contain"
                    />
                  </div>
                )}
              </div>
              <div>
                <Label>Skill India Logo / स्किल इंडिया लोगो</Label>
                <div className="flex gap-2">
                  <Input
                    value={settings.skill_india_logo_url}
                    onChange={(e) => handleChange('skill_india_logo_url', e.target.value)}
                    placeholder="https://... or upload"
                    className="flex-1"
                  />
                  <input
                    type="file"
                    ref={skillIndiaLogoInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'skill_india_logo')}
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => skillIndiaLogoInputRef.current?.click()}
                    disabled={uploading.skill_india_logo}
                    className="shrink-0"
                  >
                    {uploading.skill_india_logo ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {settings.skill_india_logo_url && (
                  <div className="mt-2 p-2 bg-slate-900 rounded-lg inline-block">
                    <img 
                      src={settings.skill_india_logo_url} 
                      alt="Skill India Logo Preview" 
                      className="h-12 object-contain"
                    />
                  </div>
                )}
              </div>
              <div>
                <Label>Watermark Logo / वॉटरमार्क (Optional)</Label>
                <div className="flex gap-2">
                  <Input
                    value={settings.watermark_url}
                    onChange={(e) => handleChange('watermark_url', e.target.value)}
                    placeholder="https://... or upload"
                    className="flex-1"
                  />
                  <input
                    type="file"
                    ref={watermarkInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'watermark')}
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => watermarkInputRef.current?.click()}
                    disabled={uploading.watermark}
                    className="shrink-0"
                  >
                    {uploading.watermark ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {settings.watermark_url && (
                  <div className="mt-2 p-2 bg-slate-100 rounded-lg inline-block">
                    <img 
                      src={settings.watermark_url} 
                      alt="Watermark Preview" 
                      className="h-12 object-contain opacity-50"
                    />
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  यदि खाली छोड़ा, तो Skill India logo watermark के रूप में use होगा
                </p>
              </div>
            </div>
          </div>

          {/* Verification Settings Card */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-4 text-white">
              <h2 className="font-bold flex items-center gap-2">
                <Shield className="w-5 h-5" /> Verification Settings
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>Verification Base URL</Label>
                <Input
                  value={settings.verification_base_url}
                  onChange={(e) => handleChange('verification_base_url', e.target.value)}
                  placeholder="https://your-domain.com"
                />
                <p className="text-xs text-slate-500 mt-1">
                  QR codes will link to: {settings.verification_base_url || '[base_url]'}/verify/[certificate_number]
                </p>
              </div>
              <div>
                <Label>Default Place of Issue</Label>
                <Input
                  value={settings.default_place}
                  onChange={(e) => handleChange('default_place', e.target.value)}
                  placeholder="e.g., Jaipur"
                />
              </div>
            </div>
          </div>

          {/* Certificate Numbering Card */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white">
              <h2 className="font-bold flex items-center gap-2">
                <Award className="w-5 h-5" /> Certificate Numbering
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>RPL Prefix</Label>
                  <Input
                    value={settings.rpl_prefix}
                    onChange={(e) => handleChange('rpl_prefix', e.target.value)}
                    placeholder="IQRA/RPL"
                  />
                </div>
                <div>
                  <Label>STT Prefix</Label>
                  <Input
                    value={settings.stt_prefix}
                    onChange={(e) => handleChange('stt_prefix', e.target.value)}
                    placeholder="IQRA/STT"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Current Year</Label>
                  <Input
                    type="number"
                    value={settings.current_year}
                    onChange={(e) => handleChange('current_year', parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <Label>RPL Counter</Label>
                  <Input
                    type="number"
                    value={settings.rpl_counter}
                    onChange={(e) => handleChange('rpl_counter', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Next: {settings.rpl_prefix}/{settings.current_year}/{String(settings.rpl_counter + 1).padStart(6, '0')}
                  </p>
                </div>
              </div>
              <div>
                <Label>STT Counter</Label>
                <Input
                  type="number"
                  value={settings.stt_counter}
                  onChange={(e) => handleChange('stt_counter', parseInt(e.target.value))}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Next: {settings.stt_prefix}/{settings.current_year}/{String(settings.stt_counter + 1).padStart(6, '0')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="mt-8 bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-500" /> Preview Certificate
          </h2>
          <div className="flex gap-4">
            <Button 
              variant="outline"
              onClick={() => window.open('/certificate/test-cert-001', '_blank')}
            >
              <Award className="w-4 h-4 mr-2" /> View Test Certificate
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.open(`/verify/${encodeURIComponent('IQRA/RPL/2026/000001')}`, '_blank')}
            >
              <Shield className="w-4 h-4 mr-2" /> Test Verification Page
            </Button>
          </div>
        </div>
      </div>
  );
};

export default AdminCertificateSettingsPage;
